import { useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { X, Plus, Trash2 } from "lucide-react";

// ---------- helpers (also exported) ----------
export const isGradient = (v) =>
  typeof v === "string" && v.trim().startsWith("linear-gradient");

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

export const hexToRgb = (hex) => {
  const h = (hex || "").replace("#", "");
  if (h.length === 3)
    return { r: parseInt(h[0] + h[0], 16), g: parseInt(h[1] + h[1], 16), b: parseInt(h[2] + h[2], 16) };
  if (h.length === 6)
    return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
  return { r: 0, g: 0, b: 0 };
};

const rgbToHex = ({ r, g, b }) =>
  "#" +
  [r, g, b]
    .map((n) => Math.round(clamp(n, 0, 255)).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();

const rgbToHsv = ({ r, g, b }) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max ? d / max : 0, v: max };
};

const hsvToRgb = ({ h, s, v }) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
};

export const parseGradient = (css) => {
  const inside = (css || "").match(/linear-gradient\((.+)\)\s*$/i);
  if (!inside)
    return { angle: 135, stops: [{ color: "#126FF2", pos: 0 }, { color: "#000000", pos: 100 }] };
  const parts = inside[1].split(/,(?![^(]*\))/).map((s) => s.trim());
  let angle = 135;
  if (parts.length && /^-?\d+(\.\d+)?deg$/i.test(parts[0])) {
    angle = parseFloat(parts.shift());
  }
  const stops = parts.map((p) => {
    const m = p.match(/(#[0-9a-f]{3,8})\s*(-?\d+(?:\.\d+)?)?%?/i);
    if (!m) return { color: "#000000", pos: 0 };
    return { color: m[1].toUpperCase(), pos: m[2] != null ? parseFloat(m[2]) : 0 };
  });
  if (stops.length < 2) stops.push({ color: "#000000", pos: 100 });
  return { angle, stops };
};

export const formatGradient = ({ angle, stops }) =>
  `linear-gradient(${angle}deg, ${stops
    .map((s) => `${s.color} ${Math.round(s.pos)}%`)
    .join(", ")})`;

// ---------- Solid picker ----------
function SolidPicker({ value, onChange }) {
  const [mode, setMode] = useState("hex");
  const safe = /^#[0-9a-f]{3,6}$/i.test(value) ? value : "#126FF2";
  const rgb = hexToRgb(safe);
  const hsv = rgbToHsv(rgb);
  const svRef = useRef();
  const hueRef = useRef();

  const commit = (h, s, v) => onChange(rgbToHex(hsvToRgb({ h, s, v })));

  const onDrag = (ref, apply) => (e) => {
    e.preventDefault();
    const update = (clientX, clientY) => {
      const r = ref.current.getBoundingClientRect();
      apply(
        clamp((clientX - r.left) / r.width, 0, 1),
        clamp((clientY - r.top) / r.height, 0, 1),
      );
    };
    update(e.clientX, e.clientY);
    const move = (ev) => update(ev.clientX, ev.clientY);
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const hexNoHash = safe.replace("#", "");
  const onHex = (raw) => {
    const clean = (raw || "").replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
    if (clean.length === 3 || clean.length === 6) onChange("#" + clean.toUpperCase());
  };
  const onRgbPart = (key, raw) => {
    const n = clamp(parseInt((raw || "").replace(/\D/g, "") || "0", 10), 0, 255);
    onChange(rgbToHex({ ...rgb, [key]: n }));
  };

  return (
    <div className="space-y-3">
      <div
        ref={svRef}
        onMouseDown={onDrag(svRef, (sx, sy) => commit(hsv.h, sx, 1 - sy))}
        className="relative w-full h-44 rounded-md cursor-crosshair select-none"
        style={{
          background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsv.h}, 100%, 50%))`,
        }}
        data-testid="color-picker-sv"
      >
        <div
          className="absolute w-4 h-4 rounded-full border-2 border-white shadow pointer-events-none"
          style={{ left: `calc(${hsv.s * 100}% - 8px)`, top: `calc(${(1 - hsv.v) * 100}% - 8px)` }}
        />
      </div>
      <div
        ref={hueRef}
        onMouseDown={onDrag(hueRef, (sx) => commit(sx * 360, hsv.s || 1, hsv.v || 1))}
        className="relative w-full h-3 rounded-full cursor-pointer select-none"
        style={{ background: "linear-gradient(to right, red, yellow, lime, cyan, blue, magenta, red)" }}
        data-testid="color-picker-hue"
      >
        <div
          className="absolute w-4 h-4 rounded-full bg-white border-2 border-gray-300 shadow pointer-events-none top-1/2 -translate-y-1/2"
          style={{ left: `calc(${(hsv.h / 360) * 100}% - 8px)` }}
        />
      </div>
      <div className="flex items-center gap-1 bg-gray-100 rounded-md p-1 text-xs">
        {["hex", "rgb"].map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            data-testid={`color-picker-tab-${m}`}
            className={`flex-1 py-1 rounded transition-colors ${
              mode === m ? "bg-white font-medium shadow-sm text-[#1a1a1a]" : "text-gray-500"
            }`}
          >
            {m.toUpperCase()}
          </button>
        ))}
      </div>
      {mode === "hex" ? (
        <div className="flex items-center bg-gray-100 rounded-md px-3 py-2">
          <span className="text-gray-400 font-mono text-sm">#</span>
          <input
            value={hexNoHash}
            onChange={(e) => onHex(e.target.value)}
            maxLength={6}
            className="flex-1 min-w-0 pl-1 bg-transparent font-mono text-sm uppercase focus:outline-none"
            data-testid="color-picker-hex-input"
          />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 text-xs">
          {["r", "g", "b"].map((k) => (
            <div key={k} className="bg-gray-100 rounded-md px-2 py-2 flex items-center gap-1">
              <span className="text-gray-400 uppercase font-mono">{k}</span>
              <input
                value={rgb[k]}
                onChange={(e) => onRgbPart(k, e.target.value)}
                inputMode="numeric"
                className="flex-1 min-w-0 bg-transparent font-mono text-center focus:outline-none"
                data-testid={`color-picker-rgb-${k}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Gradient picker ----------
function GradientPicker({ value, onChange }) {
  const current = parseGradient(value);
  const [activeStop, setActiveStop] = useState(0);
  const idx = clamp(activeStop, 0, current.stops.length - 1);
  const stop = current.stops[idx];

  const updateStop = (patch) => {
    const stops = current.stops.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    onChange(formatGradient({ ...current, stops }));
  };
  const addStop = () => {
    const stops = [...current.stops, { color: "#999999", pos: 50 }].sort((a, b) => a.pos - b.pos);
    onChange(formatGradient({ ...current, stops }));
  };
  const removeStop = () => {
    if (current.stops.length <= 2) return;
    const stops = current.stops.filter((_, i) => i !== idx);
    setActiveStop(0);
    onChange(formatGradient({ ...current, stops }));
  };

  return (
    <div className="space-y-3">
      <div
        className="relative h-10 rounded-md border border-gray-200"
        style={{ background: formatGradient(current) }}
        data-testid="gradient-preview"
      >
        {current.stops.map((s, i) => (
          <button
            key={i}
            onClick={() => setActiveStop(i)}
            data-testid={`gradient-stop-${i}`}
            className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 ${
              i === idx ? "border-white ring-2 ring-black" : "border-white"
            }`}
            style={{ left: `${s.pos}%`, background: s.color }}
            title={`${s.color} ${s.pos}%`}
          />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={Math.round(stop.pos)}
          onChange={(e) => updateStop({ pos: clamp(parseInt(e.target.value || "0", 10), 0, 100) })}
          className="w-16 bg-gray-100 rounded-md px-2 py-1 text-xs text-center focus:outline-none"
          data-testid="gradient-stop-pos"
        />
        <span className="text-xs text-gray-500">%</span>
        <div className="flex-1" />
        <button
          onClick={addStop}
          data-testid="gradient-stop-add"
          className="w-7 h-7 rounded-md hover:bg-gray-100 flex items-center justify-center"
          title="Stop toevoegen"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={removeStop}
          disabled={current.stops.length <= 2}
          data-testid="gradient-stop-remove"
          className="w-7 h-7 rounded-md hover:bg-red-100 hover:text-red-700 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
          title="Stop verwijderen"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <SolidPicker value={stop.color} onChange={(c) => updateStop({ color: c })} />
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 uppercase tracking-widest">Hoek</span>
        <input
          type="range"
          min="0"
          max="360"
          value={current.angle}
          onChange={(e) => onChange(formatGradient({ ...current, angle: parseInt(e.target.value, 10) }))}
          className="flex-1"
          data-testid="gradient-angle"
        />
        <span className="font-mono text-xs w-10 text-right">{Math.round(current.angle)}°</span>
      </div>
    </div>
  );
}

// ---------- Main picker ----------
export function ColorPicker({
  value,
  onChange,
  allowGradient = false,
  stops = null,
  onStopChange,
  trigger,
  side = "right",
  align = "start",
}) {
  const [open, setOpen] = useState(false);
  const [activeStop, setActiveStop] = useState("from");

  // --- Figma-style stops mode: single SolidPicker with FROM/TO tabs at top. ---
  if (stops) {
    const currentValue = stops[activeStop] || "#999999";
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          side={side}
          align={align}
          className="w-72 p-4 bg-white shadow-xl rounded-xl border border-gray-200"
          data-testid="color-picker-popover"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-0.5 bg-gray-100 rounded-md p-0.5 text-xs">
              {["from", "to"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setActiveStop(s)}
                  data-testid={`color-picker-stop-${s}`}
                  className={`px-3 py-1 rounded transition-colors ${
                    activeStop === s ? "bg-white font-medium shadow-sm text-[#1a1a1a]" : "text-gray-500"
                  }`}
                >
                  {s === "from" ? "From" : "To"}
                </button>
              ))}
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="color-picker-close"
              className="w-7 h-7 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-500"
              aria-label="Sluiten"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <SolidPicker
            key={activeStop}
            value={currentValue}
            onChange={(v) => onStopChange?.(activeStop, v)}
          />
        </PopoverContent>
      </Popover>
    );
  }

  const grad = isGradient(value);
  const mode = grad ? "gradient" : "solid";

  const switchMode = (next) => {
    if (next === "gradient" && !grad) {
      const safe = /^#[0-9a-f]{3,6}$/i.test(value) ? value : "#126FF2";
      onChange(`linear-gradient(135deg, ${safe} 0%, #000000 100%)`);
    } else if (next === "solid" && grad) {
      const first = parseGradient(value).stops[0]?.color || "#126FF2";
      onChange(first);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className="w-72 p-4 bg-white shadow-xl rounded-xl border border-gray-200"
        data-testid="color-picker-popover"
      >
        <div className="flex items-center justify-between mb-3">
          {allowGradient ? (
            <select
              value={mode}
              onChange={(e) => switchMode(e.target.value)}
              className="bh-select bg-gray-50 rounded-md px-3 py-1.5 text-sm border border-gray-200"
              data-testid="color-picker-mode"
            >
              <option value="solid">Solid</option>
              <option value="gradient">Gradient</option>
            </select>
          ) : (
            <div />
          )}
          <button
            onClick={() => setOpen(false)}
            data-testid="color-picker-close"
            className="w-7 h-7 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-500"
            aria-label="Sluiten"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {mode === "gradient" ? (
          <GradientPicker value={value} onChange={onChange} />
        ) : (
          <SolidPicker value={value} onChange={onChange} />
        )}
      </PopoverContent>
    </Popover>
  );
}
